import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DatingTheoryCalculator = () => {
  const [datingData, setDatingData] = useState({
    yourAge: '',
    partnerAge: '',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDatingData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setDatingData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculateDatingRange = () => {
    const { yourAge, partnerAge } = datingData;

    if (!yourAge) {
      setError('Please enter your age');
      return;
    }

    try {
      const yourAgeNum = parseFloat(yourAge);
      const partnerAgeNum = partnerAge ? parseFloat(partnerAge) : null;

      if (isNaN(yourAgeNum)) {
        setError('Please enter a valid number for your age');
        return;
      }

      // Half Plus Seven calculations
      const minAge = (yourAgeNum / 2) + 7;
      const maxAge = (yourAgeNum - 7) * 2;
      const isPartnerInRange = partnerAgeNum 
        ? partnerAgeNum >= minAge && partnerAgeNum <= maxAge 
        : null;

      const generateChartData = () => {
        return {
          labels: ['Minimum Age', 'Maximum Age', 'Your Age'],
          datasets: [
            {
              label: 'Age Range',
              data: [minAge, maxAge, yourAgeNum],
              backgroundColor: [
                'rgba(255, 99, 132, 0.5)',
                'rgba(54, 162, 235, 0.5)',
                'rgba(255, 206, 86, 0.5)'
              ],
              borderColor: [
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)',
                'rgba(255, 206, 86, 1)'
              ],
              borderWidth: 1
            }
          ]
        };
      };

      setResults({
        minAge: minAge.toFixed(1),
        maxAge: maxAge.toFixed(1),
        isPartnerInRange,
        yourAge: yourAgeNum,
        partnerAge: partnerAgeNum
      });
      setChartData(generateChartData());

    } catch (error) {
      setError('Please enter valid numbers for all fields');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Dating Theory Calculator | Relationship Age Gap Tool</title>
        <meta name="description" content="Calculate acceptable dating age ranges using the Half Plus Seven rule. Analyze if your partner falls within the recommended age range for healthy relationships." />
        <meta name="keywords" content="dating calculator, age gap calculator, relationship calculator, half plus seven rule" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-pink-500 mr-4">❤️</div>
            <h1 className="text-3xl font-bold text-gray-800">Dating Theory Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>This calculator uses the "Half Plus Seven" rule to determine:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Acceptable minimum and maximum dating ages</li>
              <li>✓ Whether your partner falls within this range</li>
              <li>✓ Visual representation of age compatibility</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Age:</label>
                <input
                  type="number"
                  id="yourAge"
                  name="yourAge"
                  value={datingData.yourAge}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your age"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Partner's Age (optional):</label>
                <input
                  type="number"
                  id="partnerAge"
                  name="partnerAge"
                  value={datingData.partnerAge}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter partner's age (optional)"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={datingData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Show detailed analysis and compatibility breakdown
                </label>
              </div>
            </div>

            <button
              onClick={calculateDatingRange}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Dating Range
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Dating Range Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Recommended Age Range</h3>
                    <p className="text-gray-600">
                      Minimum: {results.minAge} years<br />
                      Maximum: {results.maxAge} years
                    </p>
                  </div>

                  {results.partnerAge !== null && (
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Compatibility Check</h3>
                      <p className="text-gray-600">
                        {results.isPartnerInRange 
                          ? "Your partner falls within the recommended age range!" 
                          : "Your partner is outside the recommended age range according to the Half Plus Seven rule."}
                      </p>
                    </div>
                  )}
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Age'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Age Categories'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Dating Age Range Visualization',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Analysis Summary</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Your Age:</span>
                      {results.yourAge} years
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Minimum Recommended Age:</span>
                      {results.minAge} years
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Maximum Recommended Age:</span>
                      {results.maxAge} years
                    </li>
                    {results.partnerAge !== null && (
                      <li className="flex items-center">
                        <span className="font-medium text-gray-600 mr-2">Partner's Age:</span>
                        {results.partnerAge} years
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Dating Theory Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600 mb-8">
              <p>Follow these steps to use the calculator:</p>
              <ol className="list-decimal pl-6 mt-2">
                <li>Enter your age in the first field</li>
                <li>Optionally enter your partner's age</li>
                <li>Click "Calculate Dating Range"</li>
                <li>Review the recommended age range and compatibility check</li>
              </ol>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
            <div className="text-gray-600 space-y-4">
              <div>
                <h3 className="font-semibold">What is the Half Plus Seven rule?</h3>
                <p>A guideline suggesting the youngest person you should date is half your age plus seven years, and the oldest is twice your age minus fourteen years.</p>
              </div>
              <div>
                <h3 className="font-semibold">Is this rule scientifically proven?</h3>
                <p>It's a social convention rather than a scientific law, but studies have shown it reflects actual dating patterns to some extent.</p>
              </div>
              <div>
                <h3 className="font-semibold">Should I follow this rule strictly?</h3>
                <p>It's a guideline, not a strict rule. Consider your personal comfort and relationship dynamics.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">🔢</span></h2>
            <div className="text-gray-600 bg-gray-50 p-6 rounded-lg">
              <p><strong>Inputs:</strong></p>
              <ul className="list-disc pl-6">
                <li>Your Age: 30</li>
              </ul>
              <p className="mt-4"><strong>Results:</strong></p>
              <ul className="list-disc pl-6">
                <li>Minimum recommended age: 22 (30 / 2 + 7)</li>
                <li>Maximum recommended age: 46 ((30 - 7) * 2)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatingTheoryCalculator;